// ** MUI Imports
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Typography from '@mui/material/Typography'

import { StarBorder } from '@mui/icons-material'
import { Alert, Box, Divider, Grid, IconButton, Snackbar, Stack } from '@mui/material'
import { BookMinusOutline, BookPlusOutline, EyeOutline, TrashCanOutline } from 'mdi-material-ui'
import { useRouter } from 'next/router'
import { useState } from 'react'
import ConfirmDialog from 'src/@core/components/dialogs/ConfirmDialog'
import UserApi from 'src/@core/utils/api/user-api'
import { numberFormatCompact } from 'src/@core/utils/functions'
import { AlertState, BookData } from 'src/@core/utils/types'
import BookCoverImage from './BookCoverImage'

type Props = {
  book: BookData
  onRemove?: () => void
  action: 'add' | 'remove' | 'remove_history'
}

const CardBook = ({ book, onRemove, action }: Props) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [showSynopsis, setShowSynopsis] = useState(false)
  const [alertState, setAlertState] = useState<AlertState>({ show: false })
  const router = useRouter()

  const addBookToLibrary = async () => {
    setShowConfirmDialog(false)
    try {
      await UserApi.addBookToLibrary(book._id as string)
      setAlertState({ show: true, success: true, message: `The book ${book.title} was added to your library.` })
    } catch (e: any) {
      if (e?.response?.status === 401) {
        router.push('/login/')

        return
      }
      setAlertState({ show: true, success: false, message: `There was an error in our server. Please, try again...` })
    }
  }

  const removeBookFromLibrary = async () => {
    setShowConfirmDialog(false)
    try {
      await UserApi.removeBookFromLibrary(book._id as string)
      setAlertState({ show: true, success: true, message: `The book ${book.title} was removed from your library.` })
      onRemove && onRemove()
    } catch (e) {
      setAlertState({ show: true, success: false, message: `There was an error in our server. Please, try again...` })
    }
  }

  const removeBookFromHistory = async () => {
    setShowConfirmDialog(false)
    try {
      await UserApi.removeBookFromReadHistory(book._id as string)
      setAlertState({
        show: true,
        success: true,
        message: `The book ${book.title} was removed from your read history.`
      })
      onRemove && onRemove()
    } catch (e) {
      setAlertState({ show: true, success: false, message: `There was an error in our server. Please, try again...` })
    }
  }

  const handleCloseFormUpdateAlert = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }

    setAlertState({ show: false })
  }

  return (
    <Card>
      <Grid
        container
        sx={({ breakpoints }) => ({
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 4,
          [breakpoints.down('md')]: { p: 2 }
        })}
      >
        <Stack direction={'row'} spacing={2} flexGrow={1}>
          <BookCoverImage book={book} onClick={() => router.push(`/book/${book._id}/`)} />
          <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <Stack direction={'row'} justifyContent={'space-between'}>
              <Typography variant='h6' sx={{ fontWeight: 100 }}>
                {book.title}
              </Typography>
              <IconButton onClick={() => setShowConfirmDialog(true)} sx={{ width: '1.5em', height: '1.5em' }}>
                {action === 'add' ? (
                  <BookPlusOutline />
                ) : action === 'remove' ? (
                  <BookMinusOutline />
                ) : (
                  <TrashCanOutline />
                )}
              </IconButton>
            </Stack>
            <Stack spacing={2} direction='row' divider={<Divider orientation='vertical' flexItem />}>
              <Typography variant='caption' sx={{ display: 'flex', alignItems: 'center' }}>
                {book.author}
              </Typography>
              <Typography variant='caption'>{book.genre}</Typography>
            </Stack>
            <Stack spacing={2} direction='row'>
              <EyeOutline sx={{ fontSize: '1rem' }} />
              <Typography variant='caption' sx={{ display: 'flex', alignItems: 'center' }}>
                {numberFormatCompact(book.readCount as number)}
              </Typography>
              <StarBorder sx={{ fontSize: '1rem' }} />
              <Typography variant='caption' sx={{ display: 'flex', alignItems: 'center' }}>
                {book.rating}
              </Typography>
            </Stack>
            <Typography variant='caption' sx={{ display: 'flex', alignItems: 'center' }}>
              {`${book.description?.substring(0, 50)}...`}
              <Button sx={{ textTransform: 'none' }} onClick={() => setShowSynopsis(true)}>
                synopsis
              </Button>
            </Typography>
            <Box flexGrow={1} alignItems={'end'} display={'flex'}>
              <Button
                href={`/book/${book._id}/`}
                onClick={e => {
                  e.preventDefault()
                  router.push(`/book/${book._id}/`)
                }}
              >
                Read Book
              </Button>
            </Box>
          </Box>
        </Stack>
      </Grid>
      {alertState.show && (
        <Snackbar
          open={alertState.show}
          autoHideDuration={4000}
          onClose={handleCloseFormUpdateAlert}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseFormUpdateAlert}
            severity={alertState.success ? 'success' : 'error'}
            variant={'filled'}
            elevation={6}
            sx={{ width: '100%' }}
          >
            {alertState.message}
          </Alert>
        </Snackbar>
      )}
      {showConfirmDialog && (
        <ConfirmDialog
          open={showConfirmDialog}
          dialogTitle={
            action === 'add' ? 'Add to library' : action === 'remove' ? 'Remove from library' : 'Remove from history'
          }
          contentText={
            action === 'add'
              ? `Do you want to add the book ${book.title} to your library?`
              : action === 'remove'
              ? `Do you want to remove the book ${book.title} from your library?`
              : `Do you want to remove the book ${book.title} from your read history?`
          }
          confirmText={action === 'add' ? 'Add Book' : 'Remove Book'}
          cancelText={'Cancel'}
          onCancel={() => setShowConfirmDialog(false)}
          onConfirm={
            action === 'add' ? addBookToLibrary : action === 'remove' ? removeBookFromLibrary : removeBookFromHistory
          }
        ></ConfirmDialog>
      )}
      {showSynopsis && (
        <ConfirmDialog
          open={showSynopsis}
          dialogTitle={`Synopsis: ${book.title}`}
          contentText={book?.description || ''}
          confirmText={'Close'}
          onConfirm={() => setShowSynopsis(false)}
        ></ConfirmDialog>
      )}
    </Card>
  )
}

export default CardBook
